import { RoutePath } from './RoutePath';

export class RoutePrefix<
  TPrefix extends string,
  TParentPrefixWithSlash extends string = '',
> {
  public readonly prefix: TPrefix;
  public readonly parentPrefixWithSlash: TParentPrefixWithSlash;

  constructor(prefix: TPrefix, parentPrefixWithSlash?: TParentPrefixWithSlash) {
    this.prefix = prefix;
    // @ts-expect-error fallback to an empty string
    this.parentPrefixWithSlash = parentPrefixWithSlash ?? '';
  }

  /**
   * Create a route that uses this group's prefix & params
   */
  path<
    TPath extends string,
    TSearchParam extends string = never,
    TArraySearchParam extends string = never,
  >(
    path: TPath,
    searchParams?: TSearchParam[],
    arraySearchParams?: TArraySearchParam[],
  ): RoutePath<
    `${TParentPrefixWithSlash}${TPrefix}`,
    TPath,
    TSearchParam,
    TArraySearchParam
  > {
    return new RoutePath({
      prefix: this.fullPath,
      path: path,
      searchParams: searchParams ?? [],
      arraySearchParams: arraySearchParams ?? [],
    });
  }

  rootPath<
    TSearchParam extends string = never,
    TArraySearchParam extends string = never,
  >(
    searchParams?: TSearchParam[],
    arraySearchParams?: TArraySearchParam[],
  ): RoutePath<
    `${TParentPrefixWithSlash}${TPrefix}`,
    '',
    TSearchParam,
    TArraySearchParam
  > {
    return new RoutePath({
      prefix: this.fullPath,
      path: '',
      searchParams: searchParams ?? [],
      arraySearchParams: arraySearchParams ?? [],
    });
  }

  get fullPath(): `${TParentPrefixWithSlash}${TPrefix}` {
    return `${this.parentPrefixWithSlash}${this.prefix}`;
  }

  get wrapperPath(): `${TPrefix}/*` {
    return `${this.prefix}/*`;
  }

  get fullWrapperPath(): `${TParentPrefixWithSlash}${TPrefix}/*` {
    return `${this.fullPath}/*`;
  }

  subprefix<TSubgroupPath extends string>(
    path: TSubgroupPath,
  ): RoutePrefix<TSubgroupPath, `${TParentPrefixWithSlash}${TPrefix}/`> {
    return new RoutePrefix(path, `${this.fullPath}/`);
  }
}
